import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, CheckCircle, XCircle, RotateCcw, Crown } from "lucide-react";
import { QUIZ_QUESTIONS } from "@/data/staticData";
import { toast } from "sonner";

export default function QuizPage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [started, setStarted] = useState(false);

  const startQuiz = useCallback(async () => {
    try {
      const { data } = await API.get("/user/quota/quiz");
      if (!data.allowed) { toast.error(`Limite de quiz atteinte (${data.limit}/jour).`); return; }
    } catch {}
    setStarted(true);
    setCurrent(0); setScore(0); setSelected(null); setAnswered(false); setFinished(false);
  }, []);

  const handleAnswer = (idx) => {
    if (answered) return;
    setSelected(idx);
    setAnswered(true);
    if (idx === QUIZ_QUESTIONS[current].correct) setScore(s => s + 1);
  };

  const next = () => {
    if (current < QUIZ_QUESTIONS.length - 1) {
      setCurrent(c => c + 1); setSelected(null); setAnswered(false);
    } else {
      setFinished(true);
      API.post("/diagnostic", { answers: [{ quiz_score: score + (selected === QUIZ_QUESTIONS[current].correct ? 1 : 0) }] }).catch(() => {});
    }
  };

  const q = QUIZ_QUESTIONS[current];
  const progress = ((current + 1) / QUIZ_QUESTIONS.length) * 100;
  const finalScore = finished ? score : 0;
  const pct = Math.round((finalScore / QUIZ_QUESTIONS.length) * 100);

  if (!started) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="quiz-page">
        <div className="text-center animate-fade-in mt-12">
          <div className="w-16 h-16 bg-[var(--ia-navy)]/5 rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="h-8 w-8 text-[var(--ia-navy)]" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Quiz de conformité IA</h1>
          <p className="text-[var(--ia-text-muted)] mb-6">{QUIZ_QUESTIONS.length} questions pour tester vos connaissances RGPD et AI Act</p>
          <Button onClick={startQuiz} className="bg-[var(--ia-navy)]" data-testid="quiz-start-btn">
            Commencer le quiz
          </Button>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="quiz-page">
        <div className="text-center animate-fade-in mt-12">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${pct >= 70 ? "bg-green-50" : pct >= 40 ? "bg-amber-50" : "bg-red-50"}`}>
            <span className="text-3xl font-bold" style={{ color: pct >= 70 ? "var(--ia-risk-minimal)" : pct >= 40 ? "var(--ia-risk-high)" : "var(--ia-risk-unacceptable)" }}>{pct}%</span>
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {pct >= 70 ? "Excellent !" : pct >= 40 ? "Pas mal !" : "À revoir..."}
          </h2>
          <p className="text-[var(--ia-text-muted)] mb-6">{finalScore}/{QUIZ_QUESTIONS.length} bonnes reponses</p>
          <Button onClick={startQuiz} className="bg-[var(--ia-navy)]" data-testid="quiz-restart-btn">
            <RotateCcw className="h-4 w-4 mr-2" /> Recommencer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="quiz-page">
      <div className="mb-6 animate-fade-in">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-[var(--ia-muted)]">Question {current + 1}/{QUIZ_QUESTIONS.length}</span>
          <Badge variant="outline" className="text-xs">Score: {score}</Badge>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      <Card className="border-[var(--ia-border)] animate-fade-in" data-testid="quiz-question-card">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold mb-6 text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{q.question}</h2>
          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const isCorrect = i === q.correct;
              const isSelected = i === selected;
              let cls = "border-[var(--ia-border)] bg-white hover:border-slate-300";
              if (answered) {
                if (isCorrect) cls = "border-green-400 bg-green-50";
                else if (isSelected && !isCorrect) cls = "border-red-400 bg-red-50";
              }
              return (
                <button key={i} onClick={() => handleAnswer(i)} disabled={answered} data-testid={`quiz-option-${i}`}
                  className={`w-full p-4 rounded-md border text-left text-sm transition-all ${cls} flex items-center gap-3`}>
                  <span className="w-6 h-6 rounded-full border flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {answered && isCorrect ? <CheckCircle className="h-4 w-4 text-green-600" /> : answered && isSelected ? <XCircle className="h-4 w-4 text-red-600" /> : String.fromCharCode(65 + i)}
                  </span>
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>
          {answered && (
            <div className="mt-4 p-3 rounded-md bg-slate-50 border border-[var(--ia-border)] text-sm text-slate-700 animate-fade-in" data-testid="quiz-explanation">
              {q.explanation}
            </div>
          )}
          {answered && (
            <div className="mt-4 flex justify-end">
              <Button onClick={next} className="bg-[var(--ia-navy)]" data-testid="quiz-next-btn">
                {current < QUIZ_QUESTIONS.length - 1 ? "Question suivante" : "Voir les resultats"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
